import { isEmpty, getCookie, logger } from '../utils';
import jwtDecode from 'jwt-decode';
import hasPermission from './hasPermission';
import { hasRoleAccess, checkRole } from './fallbackAccess';

export {
  isEmpty,
  getCookie,
  hasPermission,
  hasRoleAccess,
  checkRole
};

/**
 * @returns {Object} user cookie payload
 */
export function getAuthUser() {
  const authCookie = getCookie('86553b39');

  return authCookie ? jwtDecode(authCookie)?.payload : null;
}

/**
 * @returns {Object} permissions cookie payload
 */
export function getPermissions() {
  const permCookie = getCookie('perm');

  return permCookie ? jwtDecode(permCookie)?.payload : null;
}

/**
 * Check if user cookie is issued for the current environment
 * @returns {boolean}
 */
export function checkForEnvMatch() {
  if (getCookie('86553b39')) {
    return getAuthUser()?.ne === process.env.REACT_APP_ENV_TAG;

  } else {
    return false;
  }
}

/**
 * @returns {number} session time in minutes
 */
export function getSessionTime() {
  const authCookie = getCookie('86553b39');

  if (authCookie) {
    const iat = jwtDecode(authCookie)?.iat;

    return iat ? Math.abs((new Date(iat * 1000).getTime() - new Date().getTime()) / 1000 / 60) : null;
  }

  return null;
}

/**
 * Check if user has access to a feature using permissions, or fallback to roles
 * @param {string} path
 * @param {string} [scope="READ"]
 * @returns {boolean}
 */
export function checkForAccess(path, scope = 'READ') {
  const ALLOW_PERMISSION_FALLBACK = process.env.REACT_APP_ALLOW_PERMISSION_FALLBACK || 'true';
  const FORCE_FALLBACK = process.env.REACT_APP_FORCE_PERMISSION_FALLBACK || 'false';

  const perm = getPermissions();
  const authUser = getAuthUser();
  const isFullAccess = checkRole(authUser, ['GLOBAL_ADMIN']) || (FORCE_FALLBACK === 'false' && perm?.permissions?.some(p => p.path === 'suite' && p.scope === 'WRITE'));
  const isCurrentGhost = !!perm?.gk || !!authUser?.gk || false;

  if (isFullAccess) {
    // Full access to all features
    logger('Full access: ', {
      FORCE_FALLBACK: FORCE_FALLBACK === 'true',
      ALLOW_PERMISSION_FALLBACK: ALLOW_PERMISSION_FALLBACK === 'true',
      path,
      scope
    });

    return true;

  } else if (path.startsWith('suite.ghosting')) {
    // Modify Ghosting access if the user is currently ghosting
    return isCurrentGhost || hasRoleAccess(authUser, path) || (FORCE_FALLBACK === 'false' && hasPermission(perm, path, scope));

  } else if (FORCE_FALLBACK === 'true' || (isEmpty(perm?.permissions) && authUser && ALLOW_PERMISSION_FALLBACK === 'true')) {
    // Access determined by role if permissions are empty
    logger('Role access: ', {
      FORCE_FALLBACK: FORCE_FALLBACK === 'true',
      ALLOW_PERMISSION_FALLBACK: ALLOW_PERMISSION_FALLBACK === 'true',
      path,
      scope
    });

    return hasRoleAccess(authUser, path);

  } else {
    // Access determined by permissions
    logger('Permission Access: ', {
      FORCE_FALLBACK: FORCE_FALLBACK === 'true',
      ALLOW_PERMISSION_FALLBACK: ALLOW_PERMISSION_FALLBACK === 'true',
      path,
      scope
    });

    return hasPermission(perm, path, scope);
  }
};

/**
 * Create feature access object for Header
 * @returns {Object}
 */
export function createFeatureAccess() {
  const access = {
    ghosting: checkForAccess('suite.ghosting'),
    vips: checkForAccess('suite.vips'),
    live: checkForAccess('suite.live'),
    analytics: checkForAccess('suite.analytics'),
    devices: checkForAccess('suite.devices'),
    api: checkForAccess('suite.api'),
    portal: checkForAccess('suite.portal'),
    education: checkForAccess('suite.education'),
    scheduler: checkForAccess('suite.scheduler'),
    prepaid_pass_manager: checkForAccess('suite.prepaid_pass_manager'),
    provisioning: checkForAccess('suite.provisioning'),
    users: checkForAccess('suite.users'),
    signal: checkForAccess('suite.signal'),
    network_diagram: checkForAccess('suite.network_diagram'),
    onboarding: checkForAccess('suite.onboarding'),
    access_control: checkForAccess('suite.access_control'),
    business_intelligence: checkForAccess('suite.business_intelligence')
  };

  return access;
}
